import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  message,
} from 'antd';

import {
  createTema,
  deleteTema,
  listEdicoes,
  listTemas,
  updateTema,
  type EdicaoAdmin,
  type Tema,
  type TemaWritePayload,
} from '../../services/admin';

interface TemaFormValues {
  nome: string;
  edicoesId?: number;
}

export const TemasCatalog: React.FC = () => {
  const [temas, setTemas] = useState<Tema[]>([]);
  const [edicoes, setEdicoes] = useState<EdicaoAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Tema | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<TemaFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTemas(await listTemas());
    } catch {
      message.error('Falha ao carregar os temas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    listEdicoes()
      .then(setEdicoes)
      .catch(() => message.error('Falha ao carregar as edições.'));
  }, [load]);

  const edicaoNome = useMemo(() => {
    const map = new Map<number, string>();
    edicoes.forEach((e) => map.set(e.id, e.nome ?? `#${e.id}`));
    return map;
  }, [edicoes]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (t: Tema) => {
    setEditing(t);
    form.setFieldsValue({ nome: t.nome ?? '', edicoesId: t.edicoesId ?? undefined });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: TemaFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload: TemaWritePayload = { nome: values.nome.trim() };
    if (values.edicoesId != null) payload.edicoesId = values.edicoesId;
    setSaving(true);
    try {
      if (editing) {
        await updateTema(editing.id, payload);
        message.success('Tema atualizado.');
      } else {
        await createTema(payload);
        message.success('Tema criado.');
      }
      setModalOpen(false);
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.msg ?? 'Falha ao salvar o tema.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t: Tema) => {
    try {
      await deleteTema(t.id);
      message.success('Tema excluído.');
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.msg ?? 'Falha ao excluir o tema.');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 64 },
    { title: 'Nome', dataIndex: 'nome', render: (v: string | null) => v ?? '—' },
    {
      title: 'Edição',
      dataIndex: 'edicoesId',
      render: (id: number | null) => (id == null ? '—' : edicaoNome.get(id) ?? `#${id}`),
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 160,
      render: (_: unknown, t: Tema) => (
        <Space>
          <Button size="small" onClick={() => openEdit(t)}>
            Editar
          </Button>
          <Popconfirm
            title="Excluir este tema?"
            okText="Excluir"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => handleDelete(t)}
          >
            <Button size="small" danger>
              Excluir
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Temas</h2>
        <Button type="primary" onClick={openCreate}>
          Novo tema
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={temas}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 600 }}
      />

      <Modal
        title={editing ? `Editar tema #${editing.id}` : 'Novo tema'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, message: 'Informe o nome do tema.' }]}
          >
            <Input placeholder="Ex.: Animais" />
          </Form.Item>
          <Form.Item name="edicoesId" label="Edição (opcional)">
            <Select
              allowClear
              showSearch
              placeholder="Vincular a uma edição"
              optionFilterProp="children"
            >
              {edicoes.map((e) => (
                <Select.Option key={e.id} value={e.id}>
                  {e.nome ?? `#${e.id}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TemasCatalog;
